import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { CadenceOverdueModal } from '@/features/availability/components/modals/CadenceOverdueModal';
import { useCadenceStatus } from '@/features/availability/hooks/useCadenceStatus';
import { useCadenceStore } from '@/features/availability/store/cadenceStore';
import { CLINICAL_ROLES } from '@/lib/rbac';
import { useAuthStore } from '@/stores/authStore';

/**
 * Refetches everything the cadence gate was blocking, the moment the gate
 * lifts.
 *
 * The BE's `requireCadenceUpToDate` answers 403 `AVAILABILITY_OVERDUE` on any
 * guarded endpoint; the api-client interceptor opens the modal AND rejects, so
 * the calling query parks in `error`. Nothing used to refetch it once the
 * practitioner fixed their availability — the only invalidation on that path is
 * `availabilityKeys.lists()`, which is not a prefix of e.g. `['clients','list',…]`.
 * So the clients screen kept showing the stale "Update your availability…"
 * error until a full page reload. (Tellingly the cadence chip *did* self-heal —
 * it rides the availability key — which proved the gap was scope, not mechanism.)
 *
 * Watching the overdue → satisfied edge here covers every resolution path:
 * "Mark all missing days OFF", "Update day-by-day" on the availability screen,
 * and a sibling tab fixing it. A fix inside the modal's handler would miss the
 * latter two.
 *
 * A bare `invalidateQueries()` is deliberate: the gate is global (it can guard
 * any endpoint) and the FE can't know which keys were blocked — the interceptor
 * sees URLs, not query keys. The blast radius is bounded: it only refetches
 * *active* queries, and only once per resolution. TanStack refetches queries in
 * `error` status too, so this is sufficient to clear the stale error.
 */
function CadenceResolutionEffect({ practitionerId }: { practitionerId: string }) {
  const queryClient = useQueryClient();
  const { overdue, isLoading } = useCadenceStatus(practitionerId);
  const wasOverdue = useRef(false);

  useEffect(() => {
    // Ignore the bootstrap: first paint reports overdue=false while loading, so
    // treating it as an edge would fire a global invalidate on every mount.
    if (isLoading) return;

    if (wasOverdue.current && !overdue) {
      wasOverdue.current = false;
      void queryClient.invalidateQueries();
      // Drop the retained missingDays so a later 403 can't render stale days.
      useCadenceStore.getState().reset();
      return;
    }
    wasOverdue.current = overdue;
  }, [overdue, isLoading, queryClient]);

  return null;
}

export function CadenceRedirectMount() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  // Only clinical practitioners are subject to the cadence gate — don't issue
  // the availability-range query for clients/admins.
  const watches = role !== null && CLINICAL_ROLES.includes(role) && Boolean(user?.id);

  return (
    <>
      {watches && user ? <CadenceResolutionEffect practitionerId={user.id} /> : null}
      <CadenceOverdueModal />
    </>
  );
}
